/**
 * 稼働開始日をまとめて変更(2026-10-09 発注者指示)。
 * スプレッドシートの「名前」「稼働日」の2列を貼り付ける → 照合の結果を確認 → 「変更する」。
 * 名前の末尾が W の行は不用品、それ以外は給湯器。保存は詳細の「稼働開始日を変更」と同じ「指定」。
 */
import { useMemo, useState } from 'react';
import Modal from '@core/components/Modal';
import { useToast } from '@core/components/Toast';
import { todayStr, toDateLabel } from '@core/lib/date';
import { BUSINESS_LABELS, type Operator } from '@core/lib/types';
import { formatCount } from '@core/utils/formatters';
import type { TrainingTrackDoc } from './training';
import { planStartDateChanges } from './trainingSheet';
import { setTrackStartDates } from './trainingRepo';

const STATUS_LABEL = { change: '変更する', same: '同じ日付', error: '変更しない' } as const;

export default function TrainingStartDateModal({ operators, existing, userId, onClose, onDone }: { operators: Operator[]; existing: TrainingTrackDoc[]; userId: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const plan = useMemo(() => (text.trim() ? planStartDateChanges(text, operators, existing, todayStr()) : []), [text, operators, existing]);
  const changes = plan.filter((p) => p.status === 'change');
  const errors = plan.filter((p) => p.status === 'error').length;

  async function run() {
    if (changes.length === 0) return;
    setBusy(true);
    try {
      const n = await setTrackStartDates(
        changes.map((c) => ({ opId: c.opId!, business: c.business, date: c.date!, before: c.current })),
        userId,
      );
      toast(`稼働開始日を ${formatCount(n)}件 変更しました`);
      onDone();
    } catch (e) {
      toast(e instanceof Error ? `変更できませんでした: ${e.message}` : '変更できませんでした');
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} width="min(860px, 94vw)">
      <h3 className="training-modal-title">稼働開始日をまとめて変更</h3>
      <p className="ui-hint">「名前」「稼働日」の2列を貼り付けてください(例: 山田　太郎⇥5/11)。名前の末尾が W の行は不用品、それ以外は給湯器の稼働開始日を変えます。</p>
      <textarea
        className="ui-input training-paste"
        rows={6}
        value={text}
        onChange={(e) => setText(e.target.value)}
        aria-label="名前と稼働日の貼り付け"
        disabled={busy}
      />
      {plan.length > 0 && (
        <>
          <p>
            変更する: <b>{formatCount(changes.length)}件</b> ・ 同じ日付: {formatCount(plan.filter((p) => p.status === 'same').length)}件
            {errors > 0 && (
              <>
                {' '}
                ・ 変更しない: <b className="training-error">{formatCount(errors)}件</b>
              </>
            )}
          </p>
          <div className="report-scroll is-auto training-preview">
            <table className="report-table no-stack">
              <caption className="sr-only">変更する内容</caption>
              <thead>
                <tr>
                  <th scope="col">貼り付けた名前</th>
                  <th scope="col">マスタ</th>
                  <th scope="col">カテゴリ</th>
                  <th scope="col">今の稼働開始</th>
                  <th scope="col">新しい稼働開始</th>
                  <th scope="col">結果</th>
                </tr>
              </thead>
              <tbody>
                {plan.map((p) => (
                  <tr key={p.line} className={p.status === 'change' ? '' : 'is-muted'}>
                    <td>{p.name}</td>
                    <td>{p.opId ? `${p.opCode} ${p.operatorName}` : '—'}</td>
                    <td>{BUSINESS_LABELS[p.business]}</td>
                    <td>{toDateLabel(p.current)}</td>
                    <td>{toDateLabel(p.date)}</td>
                    <td className={p.status === 'error' ? 'training-error' : undefined}>{p.status === 'error' ? p.reason : STATUS_LABEL[p.status]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <div className="training-actions">
        <button type="button" className="ui-btn ui-btn-primary" onClick={() => void run()} disabled={busy || changes.length === 0}>
          {busy ? '変更中…' : changes.length > 0 ? `${formatCount(changes.length)}件 変更する` : '変更する'}
        </button>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose} disabled={busy}>
          やめる
        </button>
      </div>
    </Modal>
  );
}
